import { Heading, Separator } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type Payload, type ProcedureType } from "@overmap-ai/common"
import { selectProcedureTypeById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useUserOrganizationAccess } from "../../hooks/users"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import {
	BASE_EXTENSIONS,
	LINK_NODES,
	MENTION_EXTENSIONS,
	ProcedureTypeAttachmentNode,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { EditorToolbar, NewLineFloatingMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { ProcedureTypeFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/general",
)

export const ProcedureTypeSettingsGeneralView = memo(() => {
	const { procedureTypeId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`expected procedure type to be defined, got ${procedureType}`)
	}

	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchProcedureType = useCallback(
		(procedureTypePayload: Partial<Payload<ProcedureType>>) => {
			void sdk.procedureTypes.update({ uuid: procedureType.uuid, ...procedureTypePayload })
		},
		[procedureType.uuid],
	)

	const handleDescriptionChange = useCallback(
		(description: JSONContent | undefined) => {
			handlePatchProcedureType({ description: description ? JSON.stringify(description) : "" })
		},
		[handlePatchProcedureType],
	)

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.procedureTypeAttachments.bulkAdd(
				files.map((file) => ({
					procedure_type: procedureType.uuid,
					file,
				})),
			)
		},
		[procedureType.uuid],
	)

	const editorContent = useEditorContent(procedureType.description)
	const handleEditorBlur = useEditorBlur(editorContent, handleDescriptionChange)

	const editor = useEditor(
		{
			editorProps: {
				attributes: { class: "outline-none" },
			},
			content: editorContent,
			editable: isOrganizationAdmin,
			onBlur: handleEditorBlur,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				ProcedureTypeAttachmentNode,
				ProcedureTypeFileHandler.configure({
					uuid: procedureType.uuid,
				}),
				Placeholder.configure({
					placeholder: "Enter description",
				}),
			],
		},
		[handlePatchProcedureType, isOrganizationAdmin, procedureType.uuid],
	)

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFiles(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFiles],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">General</Heading>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-hidden">
					<EditorContext value={editor}>
						<EditorToolbar editor={editor} />
						<EditorContent className="w-full grow overflow-y-auto overflow-x-hidden" editor={editor} />
						<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
					</EditorContext>
				</div>
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsGeneralView.displayName = "ProcedureTypeSettingsGeneralView"
